import { useState, useEffect, useCallback } from 'react'
import { filterByDateRange, type DateFilter } from '@/utils/dateFilters'
import { logger } from '@/utils/logger'

interface UseSlotFiltersReturn {
  selectedClasses: string[]
  dateFilter: DateFilter
  updateSelectedClasses: (classes: string[]) => void
  updateDateFilter: (filter: DateFilter) => void
  filterSlots: <T extends { id: string; date: string; class_name: string }>(slots: T[]) => T[]
}

export function useSlotFilters(): UseSlotFiltersReturn {
  const [selectedClasses, setSelectedClasses] = useState<string[]>([])
  const [dateFilter, setDateFilter] = useState<DateFilter>('all')

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search)

    const classParam = urlParams.get('classes')
    if (classParam) {
      try {
        const classes = JSON.parse(decodeURIComponent(classParam))
        if (Array.isArray(classes)) {
          setSelectedClasses(classes)
        }
      } catch (error) {
        logger.warn('Failed to parse class filter from URL', {
          component: 'useSlotFilters',
          data: error
        })
      }
    }

    const dateParam = urlParams.get('date')
    if (dateParam && ['all', 'week', 'month', 'future', 'custom'].includes(dateParam)) {
      setDateFilter(dateParam as DateFilter)

      if (dateParam === 'custom') {
        const fromDate = urlParams.get('from')
        const toDate = urlParams.get('to')
        if (fromDate && toDate) {
          localStorage.setItem('customDateFrom', fromDate)
          localStorage.setItem('customDateTo', toDate)
        }
      }
    }
  }, [])

  const updateSelectedClasses = useCallback((newClasses: string[]) => {
    setSelectedClasses(newClasses)

    const url = new URL(window.location.href)
    if (newClasses.length > 0) {
      url.searchParams.set('classes', encodeURIComponent(JSON.stringify(newClasses)))
    } else {
      url.searchParams.delete('classes')
    }

    window.history.replaceState({}, '', url.toString())
  }, [])

  const updateDateFilter = useCallback((newFilter: DateFilter) => {
    setDateFilter(newFilter)

    const url = new URL(window.location.href)
    if (newFilter !== 'all') {
      url.searchParams.set('date', newFilter)

      if (newFilter === 'custom') {
        const customFrom = localStorage.getItem('customDateFrom')
        const customTo = localStorage.getItem('customDateTo')

        if (customFrom && customTo) {
          url.searchParams.set('from', customFrom)
          url.searchParams.set('to', customTo)
        }
      } else {
        url.searchParams.delete('from')
        url.searchParams.delete('to')
      }
    } else {
      url.searchParams.delete('date')
      url.searchParams.delete('from')
      url.searchParams.delete('to')
    }

    window.history.replaceState({}, '', url.toString())
  }, [])

  const filterSlots = useCallback(<T extends { id: string; date: string; class_name: string }>(slots: T[]): T[] => {
    return slots.filter(slot => {
      if (selectedClasses.length > 0 && !selectedClasses.includes(slot.class_name)) {
        return false
      }

      if (!filterByDateRange(slot.date, dateFilter)) {
        return false
      }

      return true
    })
  }, [selectedClasses, dateFilter])

  return {
    selectedClasses,
    dateFilter,
    updateSelectedClasses,
    updateDateFilter,
    filterSlots
  }
}
